// Theme color scheme for store pages - adjustable for future customization
// Default: Purple theme based on Figma design
export const THEME_COLORS = {
	primary: '#9F7AEA',           // Main purple - icons, borders, accents
	primaryLight: '#E9D8FD',      // Light purple - backgrounds, hover states
	primaryLightHover: '#D6BCFA', // Lighter purple - hover on light backgrounds
	primaryDark: '#553C9A',       // Dark purple - for contrast
	accent: '#B521FF',            // Bright purple - CTA buttons, active states
	accentHover: '#9A1BD6',       // Darker accent for hover
	gradient: 'linear-gradient(180deg, #F7F6F3 0%, #F1E9FC 100%)', // Page background
}

export const dayLabels = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

// Format time from "HH:MM" to "H:MM AM/PM"
export function formatTime(timeStr) {
	if (!timeStr) return ''
	const [hour, minute] = timeStr.split(':').map(Number)
	const h = hour % 12 || 12
	const ampm = hour >= 12 ? 'PM' : 'AM'
	return `${h}:${minute.toString().padStart(2, '0')} ${ampm}`
}

// Get current day index in merchant's timezone
export function getCurrentDayIndex(timezone) {
	try {
		const now = new Date()
		const dayOptions = { timeZone: timezone, weekday: 'short' }
		const dayStr = now.toLocaleDateString('en-US', dayOptions)
		const dayMap = { 'Sun': 0, 'Mon': 1, 'Tue': 2, 'Wed': 3, 'Thu': 4, 'Fri': 5, 'Sat': 6 }
		return dayMap[dayStr]
	} catch {
		return new Date().getDay()
	}
}

// Get current open status for merchant
export function getOpenStatus(weeklyHours, timezone) {
	if (!weeklyHours || !timezone) return null

	try {
		// Get current time in merchant's timezone
		const now = new Date()
		const options = { timeZone: timezone, hour: '2-digit', minute: '2-digit', hour12: false }
		const timeStr = now.toLocaleTimeString('en-US', options)
		const [currentHour, currentMinute] = timeStr.split(':').map(Number)
		const currentMinutes = currentHour * 60 + currentMinute

		// Get current day of week in merchant's timezone
		const dayOptions = { timeZone: timezone, weekday: 'short' }
		const dayStr = now.toLocaleDateString('en-US', dayOptions)
		const dayMap = { 'Sun': 0, 'Mon': 1, 'Tue': 2, 'Wed': 3, 'Thu': 4, 'Fri': 5, 'Sat': 6 }
		const dayIndex = dayMap[dayStr]

		const intervals = weeklyHours[String(dayIndex)] || []

		// Check if currently open
		for (const interval of intervals) {
			const [startH, startM] = interval.start.split(':').map(Number)
			const [endH, endM] = interval.end.split(':').map(Number)
			const startMinutes = startH * 60 + startM
			const endMinutes = endH * 60 + endM

			if (currentMinutes >= startMinutes && currentMinutes < endMinutes) {
				// Currently open - format closing time
				const closeHour = endH > 12 ? endH - 12 : endH === 0 ? 12 : endH
				const closeAmPm = endH >= 12 ? 'PM' : 'AM'
				const closeMinStr = `:${endM.toString().padStart(2, '0')}`;
				return { isOpen: true, text: `Open until ${closeHour}${closeMinStr} ${closeAmPm}` }
			}
		}

		// Currently closed - find next opening time
		// First check if there's a later slot today
		for (const interval of intervals) {
			const [startH, startM] = interval.start.split(':').map(Number)
			const startMinutes = startH * 60 + startM

			if (startMinutes > currentMinutes) {
				// Found a later slot today
				const openHour = startH > 12 ? startH - 12 : startH === 0 ? 12 : startH
				const openAmPm = startH >= 12 ? 'PM' : 'AM'
				const openMinStr = `:${startM.toString().padStart(2, '0')}`;
				return {
					isOpen: false,
					text: 'Closed',
					nextOpen: `${openHour}${openMinStr}${openAmPm}`
				}
			}
		}

		// Check future days (up to 7 days ahead)
		for (let i = 1; i <= 7; i++) {
			const nextDayIndex = (dayIndex + i) % 7
			const nextIntervals = weeklyHours[String(nextDayIndex)] || []

			if (nextIntervals.length > 0) {
				const firstInterval = nextIntervals[0]
				const [startH, startM] = firstInterval.start.split(':').map(Number)
				const openHour = startH > 12 ? startH - 12 : startH === 0 ? 12 : startH
				const openAmPm = startH >= 12 ? 'PM' : 'AM'
				const openMinStr = `:${startM.toString().padStart(2, '0')}`;
				const nextDayName = dayLabels[nextDayIndex]

				return {
					isOpen: false,
					text: 'Closed',
					nextOpen: `${openHour}${openMinStr}${openAmPm} ${nextDayName}`
				}
			}
		}

		return { isOpen: false, text: 'Closed' }
	} catch (e) {
		return null
	}
}

// Format price from cents to dollars
export const formatPrice = (priceInCents, currency = 'usd') => {
	if (!priceInCents && priceInCents !== 0) return 'Free'
	const currencySymbols = {
		usd: '$',
		cad: '$',
		gbp: '£',
		eur: '€',
		aud: '$',
		nzd: '$'
	}
	const symbol = currencySymbols[currency?.toLowerCase()] || '$'
	return `${symbol}${(priceInCents / 100).toFixed(2)}`
}
